import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { UPLOADS_URL } from '../api';

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];
const CANADIAN_PROVINCES = ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Northwest Territories','Nova Scotia','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'];
const PRODUCT_TYPE_OPTIONS = ['Masks','Suits','Accessories','Other'];
const COMMISSION_OPTIONS = ['Open','Closed','Waitlist'];
const MAX_PHOTOS = 6;

function CostumeRegistrationForm({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;
  const isEdit = !!editData;

  const [formData, setFormData] = useState({
    display_name: '',
    full_name: '',
    email: '',
    phone: '',
    mailing_street1: '',
    mailing_street2: '',
    mailing_city: '',
    mailing_state: '',
    mailing_zip: '',
    country: 'United States',
    city: '',
    state: '',
    bio: '',
    products_description: '',
    how_to_purchase: '',
    product_types: [],
    commission_status: 'Open',
    price_range: '',
    turnaround_time: '',
    ships_to: '',
    website: '',
    facebook: '',
    instagram: '',
    tiktok: '',
    youtube: '',
    other_social: '',
    publish_full_name: false,
    publish_email: false,
    publish_phone: false,
    publish_mailing_address: false,
    comments: ''
  });

  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (editData) {
      const types = editData.product_types
        ? editData.product_types.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      setFormData({
        display_name: editData.display_name || '',
        full_name: editData.full_name || '',
        email: editData.email || '',
        phone: editData.phone || '',
        mailing_street1: '',
        mailing_street2: '',
        mailing_city: '',
        mailing_state: '',
        mailing_zip: '',
        country: editData.country || 'United States',
        city: editData.city || '',
        state: editData.state || '',
        bio: editData.bio || '',
        products_description: editData.products_description || '',
        how_to_purchase: editData.how_to_purchase || '',
        product_types: types,
        commission_status: editData.commission_status || 'Open',
        price_range: editData.price_range || '',
        turnaround_time: editData.turnaround_time || '',
        ships_to: editData.ships_to || '',
        website: editData.website || '',
        facebook: editData.facebook || '',
        instagram: editData.instagram || '',
        tiktok: editData.tiktok || '',
        youtube: editData.youtube || '',
        other_social: editData.other_social || '',
        publish_full_name: !!editData.publish_full_name,
        publish_email: !!editData.publish_email,
        publish_phone: !!editData.publish_phone,
        publish_mailing_address: !!editData.publish_mailing_address,
        comments: editData.comments || ''
      });
      setExistingPhotos(editData.photos || []);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleProductTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      product_types: prev.product_types.includes(type)
        ? prev.product_types.filter(t => t !== type)
        : [...prev.product_types, type]
    }));
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/photos/${photoId}`, { headers: { Authorization: `Bearer ${token}` } });
      setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (err) {
      alert('Failed to delete photo: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSetPrimary = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/photos/${photoId}/primary`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const photo = existingPhotos.find(p => p.id === photoId);
      const rest = existingPhotos.filter(p => p.id !== photoId);
      setExistingPhotos([photo, ...rest]);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const validate = () => {
    if (!formData.display_name.trim()) return 'Display name is required.';
    if (!formData.country) return 'Country is required.';
    if (!formData.city.trim()) return 'City is required.';
    if (!formData.state.trim()) return 'State / Province is required.';
    if (!formData.bio.trim()) return 'A description of what you do is required.';
    if (!formData.products_description.trim()) return 'A product description is required.';
    if (!formData.how_to_purchase.trim()) return 'Purchase instructions are required.';
    if (formData.product_types.length === 0) return 'Please select at least one product type.';
    const hasLink = formData.website || formData.facebook || formData.instagram || formData.tiktok || formData.youtube || formData.other_social;
    if (!hasLink) return 'At least one website or social media link is required.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); window.scrollTo(0, 0); return; }
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const data = new FormData();
      Object.entries(formData).forEach(([key, val]) => {
        if (key === 'product_types') data.append('product_types', val.join(', '));
        else data.append(key, val);
      });
      newPhotos.forEach(f => data.append('photos', f));
      if (isEdit) {
        await api.put(`/api/costumes/${editData.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Registration updated! It will be reviewed before publishing.');
      } else {
        await api.post('/api/costumes/register', data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Registration submitted! It will be reviewed before publishing.');
      }
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      setError('Submission failed: ' + (err.response?.data?.error || err.message));
      window.scrollTo(0, 0);
    } finally {
      setSubmitting(false);
    }
  };

  const totalPhotos = existingPhotos.length + newPhotos.length;
  const stateOptions = formData.country === 'Canada' ? CANADIAN_PROVINCES : US_STATES;

  return (
    <div className="form-page">
      <div className="form-container">
        <h1>{isEdit ? 'Edit Costume Maker Registration' : 'Register as a Costume Maker'}</h1>
        <p>
          Showcase your Krampus costume making work to the community. All submissions are reviewed before publishing.
          Must be based in the United States or Canada.
        </p>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>

          {/* DISPLAY INFO */}
          <div className="form-section">
            <h2>Display Information</h2>
            <div className="form-group">
              <label>Display Name / Business Name <span className="required">*</span></label>
              <input name="display_name" value={formData.display_name} onChange={handleChange}
                placeholder="How you want to appear publicly" />
            </div>
            <div className="form-group">
              <label>Full Name <span className="text-muted text-sm">(optional)</span></label>
              <input name="full_name" value={formData.full_name} onChange={handleChange} placeholder="Your legal name" />
              <label className="checkbox-label">
                <input type="checkbox" name="publish_full_name" checked={formData.publish_full_name} onChange={handleChange} />
                Show full name publicly
              </label>
            </div>
          </div>

          {/* CONTACT */}
          <div className="form-section">
            <h2>Contact Information</h2>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
              <label className="checkbox-label">
                <input type="checkbox" name="publish_email" checked={formData.publish_email} onChange={handleChange} />
                Show email publicly
              </label>
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} />
              <label className="checkbox-label">
                <input type="checkbox" name="publish_phone" checked={formData.publish_phone} onChange={handleChange} />
                Show phone number publicly
              </label>
            </div>
            <div className="form-group">
              <label>Mailing Address <span className="text-muted text-sm">(optional)</span></label>
              <input className="mb-1" name="mailing_street1" value={formData.mailing_street1} onChange={handleChange} placeholder="Street Address" />
              <input className="mb-1" name="mailing_street2" value={formData.mailing_street2} onChange={handleChange} placeholder="Apt, Suite, Unit (optional)" />
              <div className="field-grid-3 mb-1">
                <input name="mailing_city" value={formData.mailing_city} onChange={handleChange} placeholder="City" />
                <input name="mailing_state" value={formData.mailing_state} onChange={handleChange} placeholder="State/Province" />
                <input name="mailing_zip" value={formData.mailing_zip} onChange={handleChange} placeholder="ZIP/Postal" />
              </div>
              <label className="checkbox-label">
                <input type="checkbox" name="publish_mailing_address" checked={formData.publish_mailing_address} onChange={handleChange} />
                Show mailing address publicly
              </label>
            </div>
          </div>

          {/* LOCATION */}
          <div className="form-section">
            <h2>Location <span className="required">*</span></h2>
            <p className="field-note">Must be based in the United States or Canada.</p>
            <div className="form-group">
              <label>Country <span className="required">*</span></label>
              <select name="country" value={formData.country} onChange={handleChange}>
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
              </select>
            </div>
            <div className="field-grid-2">
              <div className="form-group">
                <label>City <span className="required">*</span></label>
                <input name="city" value={formData.city} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>State / Province <span className="required">*</span></label>
                <select name="state" value={formData.state} onChange={handleChange}>
                  <option value="">Select...</option>
                  {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ABOUT YOUR WORK */}
          <div className="form-section">
            <h2>About Your Work</h2>
            <div className="form-group">
              <label>About You / What You Do <span className="required">*</span></label>
              <textarea name="bio" value={formData.bio} onChange={handleChange} rows="4"
                placeholder="Tell potential buyers about your background, experience, and what makes your work unique..." />
            </div>
            <div className="form-group">
              <label>Product Description <span className="required">*</span></label>
              <textarea name="products_description" value={formData.products_description} onChange={handleChange} rows="4"
                placeholder="Describe your products — materials, construction, customization options, etc..." />
            </div>
            <div className="form-group">
              <label>Product Types <span className="required">*</span> <span className="text-muted text-sm">(select all that apply)</span></label>
              <div className="checkbox-group">
                {PRODUCT_TYPE_OPTIONS.map(type => (
                  <label key={type} className="checkbox-option">
                    <input type="checkbox" checked={formData.product_types.includes(type)} onChange={() => handleProductTypeToggle(type)} />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* PURCHASING */}
          <div className="form-section">
            <h2>Purchasing</h2>
            <div className="form-group">
              <label>How to Purchase / Commission <span className="required">*</span></label>
              <textarea name="how_to_purchase" value={formData.how_to_purchase} onChange={handleChange} rows="3"
                placeholder="Explain how buyers can place an order — e.g. DM on Instagram, email, Etsy shop link..." />
            </div>
            <div className="form-group">
              <label>Commission Status <span className="required">*</span></label>
              <div className="checkbox-group mt-1">
                {COMMISSION_OPTIONS.map(opt => (
                  <label key={opt} className="checkbox-option">
                    <input type="radio" name="commission_status" value={opt} checked={formData.commission_status === opt} onChange={handleChange} />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className="field-grid-2">
              <div className="form-group">
                <label>Price Range <span className="text-muted text-sm">(optional)</span></label>
                <select name="price_range" value={formData.price_range} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="Budget">Budget</option>
                  <option value="Mid-range">Mid-range</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ships To <span className="text-muted text-sm">(optional)</span></label>
                <select name="ships_to" value={formData.ships_to} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="United States Only">United States Only</option>
                  <option value="Canada Only">Canada Only</option>
                  <option value="US & Canada">US &amp; Canada</option>
                  <option value="International">International</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Typical Turnaround / Lead Time <span className="text-muted text-sm">(optional)</span></label>
              <input name="turnaround_time" value={formData.turnaround_time} onChange={handleChange} placeholder="e.g. 8-12 weeks" />
            </div>
          </div>

          {/* LINKS */}
          <div className="form-section">
            <h2>Website and Social Media</h2>
            <p className="field-note">At least one link is required.</p>
            <div className="form-group">
              <label>Website</label>
              <input name="website" value={formData.website} onChange={handleChange} placeholder="https://" />
            </div>
            <div className="field-grid-2">
              <div className="form-group">
                <label>Facebook</label>
                <input name="facebook" value={formData.facebook} onChange={handleChange} placeholder="https://facebook.com/..." />
              </div>
              <div className="form-group">
                <label>Instagram</label>
                <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="https://instagram.com/..." />
              </div>
              <div className="form-group">
                <label>TikTok</label>
                <input name="tiktok" value={formData.tiktok} onChange={handleChange} placeholder="https://tiktok.com/..." />
              </div>
              <div className="form-group">
                <label>YouTube</label>
                <input name="youtube" value={formData.youtube} onChange={handleChange} placeholder="https://youtube.com/..." />
              </div>
            </div>
            <div className="form-group">
              <label>Other <span className="text-muted text-sm">(Etsy, Ko-fi, etc.)</span></label>
              <input name="other_social" value={formData.other_social} onChange={handleChange} placeholder="https://..." />
            </div>
          </div>

          {/* PHOTOS */}
          <div className="form-section">
            <h2>Photos (up to {MAX_PHOTOS})</h2>
            <p className="field-note">
              Upload up to {MAX_PHOTOS} photos of your work. JPG/PNG, max 50MB each. The first photo will be your primary display image.
            </p>
            {isEdit && existingPhotos.length > 0 && (
              <div className="form-group">
                <label>Current Photos</label>
                <div className="photo-grid">
                  {existingPhotos.map((photo, i) => (
                    <div key={photo.id || i} className={`photo-thumb${i === 0 ? ' primary-photo' : ''}`}>
                      <img src={`${UPLOADS_URL}/${photo.filename || photo}`} alt={`Photo ${i + 1}`} />
                      {i === 0 && <span className="photo-primary-badge">Primary</span>}
                      <div className="photo-thumb-overlay">
                        {photo.id && i !== 0 && (
                          <button type="button" className="photo-thumb-btn" onClick={() => handleSetPrimary(photo.id)}>Set Primary</button>
                        )}
                        {photo.id && (
                          <button type="button" className="photo-thumb-btn" onClick={() => handleDeletePhoto(photo.id)}>Delete</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {totalPhotos < MAX_PHOTOS ? (
              <div className="form-group">
                <label>{isEdit ? `Add More Photos (${totalPhotos}/${MAX_PHOTOS} used)` : `Upload Photos (${totalPhotos}/${MAX_PHOTOS})`}</label>
                <input type="file" accept="image/*" multiple
                  onChange={e => {
                    const files = Array.from(e.target.files);
                    const available = MAX_PHOTOS - existingPhotos.length;
                    setNewPhotos(files.slice(0, available));
                  }} />
                {newPhotos.length > 0 && (
                  <p className="field-note">{newPhotos.length} file{newPhotos.length !== 1 ? 's' : ''} selected</p>
                )}
              </div>
            ) : (
              <p className="field-note">Maximum of {MAX_PHOTOS} photos reached.</p>
            )}
          </div>

          {/* COMMENTS */}
          <div className="form-section">
            <h2>Additional Comments</h2>
            <div className="form-group">
              <label>Comments for the Reviewer <span className="text-muted text-sm">(optional)</span></label>
              <textarea name="comments" value={formData.comments} onChange={handleChange} rows="3"
                placeholder="Anything you would like to share with the admin reviewing your submission..." />
            </div>
          </div>

          <div className="form-trust-block">
            <div className="form-trust-item"><span className="trust-icon">🔍</span> All submissions reviewed before publishing</div>
            <div className="form-trust-item"><span className="trust-icon">✏️</span> You can edit your listing after approval</div>
            <div className="form-trust-item"><span className="trust-icon">⏱</span> Typical review time: 1–3 days</div>
          </div>

          <div className="form-actions">
            <button type="submit" className={`btn btn-primary btn-large${submitting ? ' btn-loading' : ''}`} disabled={submitting}>
              {submitting ? '' : isEdit ? 'Update Registration' : 'Submit Registration'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
              Cancel
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default CostumeRegistrationForm;
